import React from "react";
import cs from "classnames";

import {
  getContrastTooltipMarginTop,
  getContrastTooltipStyles,
} from "./contrastTooltipStyles";

import "./ContrastName.scss";

const PRO_COLOR = "#121212";
const CHAR_WIDTH_RATIO = 0.55;
const DEFAULT_FONT_WEIGHT = 400;
const CLIENT_FONT_WEIGHT = 500;
const DEFAULT_FONT_FAMILY = '"Sofia Pro", "Open Sans", sans-serif';
const CLIENT_FONT_FAMILY = '"FuturaEF", "Sofia Pro", "Open Sans", sans-serif';

const textWidthCache = new Map<string, number>();
let measureCtx: CanvasRenderingContext2D | null = null;

const getMeasureContext = () => {
  if (measureCtx || typeof document === "undefined") {
    return measureCtx;
  }

  measureCtx = document.createElement("canvas").getContext("2d");
  return measureCtx;
};

const getTextWidth = (
  value: string,
  fontSize: number,
  fontWeight: number,
  fontFamily: string
) => {
  const key = `${fontSize}-${fontWeight}-${fontFamily}-${value}`;
  const cachedWidth = textWidthCache.get(key);
  if (cachedWidth !== undefined) {
    return cachedWidth;
  }

  const context = getMeasureContext();
  if (!context) {
    const fallbackWidth = value.length * fontSize * CHAR_WIDTH_RATIO;
    textWidthCache.set(key, fallbackWidth);
    return fallbackWidth;
  }

  context.font = `${fontWeight} ${fontSize}px ${fontFamily}`;
  const measuredWidth = context.measureText(value).width;
  textWidthCache.set(key, measuredWidth);
  return measuredWidth;
};

interface Props {
  name: string;
  className?: string;
  scaleFactor?: number;
  sizeRatio?: number;
  isClientApp?: boolean;
  isMarker?: boolean;
}

export const ContrastName = ({
  name,
  className,
  scaleFactor = 0.7,
  sizeRatio = 1,
  isClientApp = false,
  isMarker = false,
}: Props) => {
  const styles = getContrastTooltipStyles(scaleFactor);
  const baseHeight = parseFloat(styles.height);
  const baseFontSize = parseFloat(styles.fontSize);
  const paddingParts = styles.padding.split(" ");
  const basePaddingX = parseFloat(paddingParts[1] || paddingParts[0]);
  const baseBorderRadius = parseFloat(styles.borderRadius);

  const contentHeight = baseHeight * sizeRatio;
  const fontSize = baseFontSize * sizeRatio;
  const paddingX = basePaddingX * sizeRatio;
  const fontFamily = isClientApp ? CLIENT_FONT_FAMILY : DEFAULT_FONT_FAMILY;
  const fontWeight = isClientApp ? CLIENT_FONT_WEIGHT : DEFAULT_FONT_WEIGHT;
  const borderRadius = Math.min(
    baseBorderRadius * sizeRatio,
    contentHeight / 2
  );
  const textWidth = getTextWidth(name, fontSize, fontWeight, fontFamily);
  const contentWidth = Math.ceil(textWidth + paddingX * 2);

  return (
    <svg
      width={contentWidth}
      height={contentHeight}
      className={cs("ContrastName", className)}
      style={{
        ...(isMarker
          ? { marginTop: getContrastTooltipMarginTop(scaleFactor) }
          : {}),
      }}
    >
      <rect
        x="0"
        y="0"
        width={contentWidth}
        height={contentHeight}
        rx={borderRadius}
        fill={PRO_COLOR}
        opacity={0.8}
      />
      <text
        x={contentWidth / 2}
        y={contentHeight / 2}
        textAnchor="middle"
        dominantBaseline="middle"
        fill="white"
        dy="0.11em"
        fontWeight={fontWeight}
        style={{ fontSize, fontFamily }}
      >
        {name}
      </text>
    </svg>
  );
};
